/**
 * The map renderer.
 *
 * Canvas 2D, drawn in layers, culled to the viewport. On a huge map only about six
 * hundred of twelve thousand tiles are ever on screen, so the cost of a frame is set
 * by the window size rather than the world size — which is what lets the same
 * renderer handle every map size without special cases.
 *
 * It reads the game state and owns none of it. Nothing here mutates a tile.
 *
 * FOG IS APPLIED BY OMISSION, not by painting over. Unknown tiles are simply never
 * drawn, so the deep-water backdrop shows through and nothing — no river, no
 * resource, no unit — can leak information about ground the player has not seen.
 * Remembered tiles are drawn and then dimmed. Doing it the other way round, drawing
 * everything and covering it up, is how a resource icon ends up faintly visible
 * through the fog.
 */

import type { Content } from '../../content/schema/index.js';
import { hexCorners } from '../core/hex/index.js';
import { neighborsByDirection, tileIndex } from '../core/state/grid.js';
import { VIS_EXPLORED, VIS_UNKNOWN, type Id, type TileGrid } from '../core/state/types.js';
import type { Scene, SceneUnit } from './scene.js';
import { tileCentre, visibleRange, worldToScreen, type Camera } from './camera.js';
import { drawResourceIcon } from './icons.js';
import { drawImprovementArt } from './improvements.js';
import {
  canopyBlobs,
  edgeStipple,
  frontierPath,
  groundColour,
  groundMottle,
  hexEdgeCorners,
  parseHex,
  reliefFor,
  riverBend,
  riverNode,
  ridges,
  scatterFor,
  scatterPoints,
  shade,
  tileNoise,
  toCss,
  waterColour,
  wobbleEdge,
  type Point,
  type ReliefKind,
  type ScatterKind,
} from './cartography.js';

/**
 * Screen angle of each hex direction, matching HEX_DIRECTIONS order (E, NE, NW, W,
 * SW, SE). Not simply `60 * d`: the axial direction list runs anticlockwise on screen
 * for the northern half, so a naive multiple mirrors four of the six.
 */
const DIRECTION_ANGLES = [0, -60, -120, 180, 120, 60].map((deg) => (deg * Math.PI) / 180);

/** Below this tile size, per-tile detail is smaller than a few pixels and only adds noise. */
const DETAIL_TILE_SIZE = 14;
const GRID_TILE_SIZE = 18;
/** Scatter is the first thing to go as you zoom out; it is the densest and least load-bearing. */
const SCATTER_TILE_SIZE = 20;
/** Coastlines are the last to go, because they are what makes the world read as shaped. */
const COAST_TILE_SIZE = 8;
/**
 * Biome dithering is the most expensive pass per tile and the one that helps least at
 * a distance, where the boundaries it softens are only a pixel or two wide anyway.
 */
const FEATHER_TILE_SIZE = 18;
/** Relief needs a few pixels of height before a lit face and a shaded one differ at all. */
const RELIEF_TILE_SIZE = 12;
/**
 * Below this a resource picture is a dozen pixels of mush, and the old coloured dot is
 * the more legible mark. Above it the picture says *what* without a hover.
 */
const ICON_TILE_SIZE = 24;

export interface RenderOptions {
  showGrid: boolean;
  showRivers: boolean;
  showResources: boolean;
  /** Tile the pointer is over, drawn with a highlight. */
  hover: { col: number; row: number } | null;
  /** Unit currently selected, drawn with a ring. */
  selectedUnit: Id | null;
  /** Tiles the selected unit could reach this turn. */
  reachable: ReadonlySet<number> | null;
  /** Remaining tiles of the selected unit's standing order. */
  path: readonly number[] | null;
  /** Tint tiles by the civilisation that owns them. */
  showBorders: boolean;
  /** Mark the tiles this city's citizens are working. */
  workedBy: Id | null;
  /** Cities of the viewer's civ that are building nothing, marked so on the map. */
  idleCities: ReadonlySet<Id> | null;
  /** Whether the viewer can see this resource; strategic ones stay hidden until revealed (ADR-0063). */
  resourceSeen?: (resource: number) => boolean;
}

export interface RenderStats {
  tilesDrawn: number;
  frameMs: number;
}

export function renderMap(
  ctx: CanvasRenderingContext2D,
  scene: Scene,
  content: Content,
  camera: Camera,
  options: RenderOptions,
): RenderStats {
  const map = scene.map;
  const started = performance.now();
  /** What the viewer knows about a tile — the scene's answer, never worked out here. */
  const seen = scene.seen;
  const range = visibleRange(camera, map);
  const size = camera.tileSize;
  const corners = hexCorners(size);

  // Deep water beyond the poles, so the world reads as a globe rather than a
  // rectangle floating in nothing.
  // The empty edges and unexplored land are warm parchment, under the house-style
  // interface (ADR-0074; Mark chose it over the old navy).
  ctx.fillStyle = '#cdc6b3';
  ctx.fillRect(0, 0, camera.viewportWidth, camera.viewportHeight);

  const detail = size >= DETAIL_TILE_SIZE;
  let tilesDrawn = 0;
  /** Reused across every pass that needs a tile's neighbours; allocating per tile is not free. */
  const neighbourScratch = new Int32Array(6);

  const improvementAt = (index: number): number => scene.improvement(index);
  /** Whether the viewer believes a city stands here — live where visible, remembered elsewhere. */
  const cityAt = (index: number): boolean => scene.city(index) !== null;

  // ── terrain ─────────────────────────────────────────────────────────────────
  for (let row = range.minRow; row <= range.maxRow; row++) {
    for (let rawCol = range.minCol; rawCol <= range.maxCol; rawCol++) {
      // The raw column may be negative or past the map's width; wrapping it here is
      // what makes the date line invisible when the view straddles it.
      const col = ((rawCol % map.width) + map.width) % map.width;
      const index = tileIndex(map, col, row);
      if (index === -1) continue;
      if (seen(index) === VIS_UNKNOWN) continue;

      const centre = tileCentre(rawCol, row, size);
      const screen = worldToScreen(camera, centre.x, centre.y);
      const terrain = content.terrain.get(map.terrain[index]!);
      const feature = content.features.get(map.feature[index]!);

      ctx.beginPath();
      ctx.moveTo(screen.x + corners[0]!.x, screen.y + corners[0]!.y);
      for (let i = 1; i < 6; i++) ctx.lineTo(screen.x + corners[i]!.x, screen.y + corners[i]!.y);
      ctx.closePath();

      // Water and land are coloured by different rules. Water cares about depth and
      // about how much land it touches — the pale shoal band round every coast is what
      // makes a continent sit *in* the sea rather than next to it. Land cares about
      // relief and about its feature, which TINTS the terrain rather than replacing it,
      // so forest on tundra and forest on plains stop looking identical.
      const colour = terrain.water
        ? waterColour(terrain.colour, map.elevation[index]!, landNeighbours(map, content, index, neighbourScratch), index)
        : groundColour(
            terrain.colour,
            feature.key !== 'none' && feature.colour.length >= 7 ? feature.colour : null,
            map.elevation[index]!,
            index,
          );
      ctx.fillStyle = colour;
      ctx.fill();
      // Stroke in the fill colour: adjacent anti-aliased edges otherwise leave
      // hairline seams that read as gaps in the grid.
      ctx.strokeStyle = colour;
      ctx.lineWidth = 1;
      ctx.stroke();

      tilesDrawn++;
    }
  }

  // ── mottle ──────────────────────────────────────────────────────────────────
  // Soft patches of light and shade inside each tile. The per-tile jitter in the fill
  // stops one field looking like the next; this stops each field being flat inside,
  // which is the thing the eye actually reads as "a cell in a spreadsheet". Neutral
  // white and black rather than colour, so one pass covers every terrain, and batched
  // into two fill styles for the whole screen.
  if (size >= DETAIL_TILE_SIZE) {
    const light: Point[] = [];
    const dark: Point[] = [];
    const radii: number[] = [];
    const darkRadii: number[] = [];

    forEachVisibleTile(map, camera, (col, rawCol, row) => {
      const index = tileIndex(map, col, row);
      if (index === -1 || seen(index) === VIS_UNKNOWN) return;
      // Land only. Open water carries nothing else, so the same patches that read as
      // variation on a field read as visible discs on the sea — depth and the shoal
      // band already give water all the variation it needs.
      if (content.terrain.get(map.terrain[index]!).water) return;
      const centre = tileCentre(rawCol, row, size);
      const screen = worldToScreen(camera, centre.x, centre.y);
      for (const patch of groundMottle(index, 3, size)) {
        const point = { x: screen.x + patch.x, y: screen.y + patch.y };
        if (patch.shade > 0) {
          light.push(point);
          radii.push(patch.r);
        } else {
          dark.push(point);
          darkRadii.push(patch.r);
        }
      }
    });

    ctx.fillStyle = 'rgba(255, 252, 240, 0.07)';
    ctx.beginPath();
    for (let i = 0; i < light.length; i++) {
      const p = light[i]!;
      ctx.moveTo(p.x + radii[i]!, p.y);
      ctx.arc(p.x, p.y, radii[i]!, 0, Math.PI * 2);
    }
    ctx.fill();

    ctx.fillStyle = 'rgba(16, 20, 12, 0.07)';
    ctx.beginPath();
    for (let i = 0; i < dark.length; i++) {
      const p = dark[i]!;
      ctx.moveTo(p.x + darkRadii[i]!, p.y);
      ctx.arc(p.x, p.y, darkRadii[i]!, 0, Math.PI * 2);
    }
    ctx.fill();
  }

  // ── coastlines ──────────────────────────────────────────────────────────────
  // The largest single readability win, and the reason the old map read as a field of
  // coloured cells rather than a world: a darker stroke on every land edge that faces
  // water. Drawn edge by edge rather than as a tile outline, so only the shore is
  // marked and the interior stays clean.
  if (size >= COAST_TILE_SIZE) {
    const neighbours = new Int32Array(6);
    // Enough segments to break the straight line, few enough that the shore does not
    // look frayed. The amplitude is a fraction of tile size so it holds at every zoom.
    const segments = size >= DETAIL_TILE_SIZE ? 4 : 2;
    const amplitude = size * 0.07;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    forEachVisibleTile(map, camera, (col, rawCol, row) => {
      const index = tileIndex(map, col, row);
      if (index === -1 || seen(index) === VIS_UNKNOWN) return;
      if (content.terrain.get(map.terrain[index]!).water) return;

      neighborsByDirection(map, index, neighbours);
      const centre = tileCentre(rawCol, row, size);
      const screen = worldToScreen(camera, centre.x, centre.y);

      // All of this tile's shore edges in one path, so surf and shore are each a
      // single stroke rather than two per edge.
      let any = false;
      ctx.beginPath();
      for (let d = 0; d < 6; d++) {
        const other = neighbours[d]!;
        // The edge of the world counts as shore too — otherwise the poles float.
        const isShore = other === -1 || content.terrain.get(map.terrain[other]!).water;
        if (!isShore) continue;

        const [ai, bi] = hexEdgeCorners(d);
        const line = wobbleEdge(index, d, corners[ai]!, corners[bi]!, segments, amplitude);
        ctx.moveTo(screen.x + line[0]!.x, screen.y + line[0]!.y);
        for (let i = 1; i < line.length; i++) ctx.lineTo(screen.x + line[i]!.x, screen.y + line[i]!.y);
        any = true;
      }
      if (!any) return;

      // Surf first, wide and pale, then the shore itself. Two strokes of one path: the
      // bright edge is what separates land from its own shadow in the water.
      ctx.strokeStyle = 'rgba(214, 240, 242, 0.38)';
      ctx.lineWidth = Math.max(1.5, size * 0.2);
      ctx.stroke();
      ctx.strokeStyle = 'rgba(16, 30, 42, 0.78)';
      ctx.lineWidth = Math.max(1, size * 0.075);
      ctx.stroke();
    });
  }

  // ── biome feathering ────────────────────────────────────────────────────────
  // Specks of the neighbour's ground sown just inside each edge that faces different
  // country. Biomes do not stop at a line, and drawing them as though they do was most
  // of what made the world read as a mosaic of coloured cells. Batched by colour —
  // there are only ever a handful of terrains on screen — so the whole pass is a few
  // fill calls rather than one per speck.
  if (size >= FEATHER_TILE_SIZE) {
    const buckets = new Map<string, Point[]>();
    const depth = size * 0.46;

    forEachVisibleTile(map, camera, (col, rawCol, row) => {
      const index = tileIndex(map, col, row);
      if (index === -1 || seen(index) === VIS_UNKNOWN) return;
      const terrain = content.terrain.get(map.terrain[index]!);
      if (terrain.water) return;

      neighborsByDirection(map, index, neighbourScratch);
      const centre = tileCentre(rawCol, row, size);
      const screen = worldToScreen(camera, centre.x, centre.y);

      for (let d = 0; d < 6; d++) {
        const other = neighbourScratch[d]!;
        if (other === -1 || seen(other) === VIS_UNKNOWN) continue;
        const neighbour = content.terrain.get(map.terrain[other]!);
        // The coastline already marks the water's edge, and marking it twice muddies it.
        if (neighbour.water || neighbour.key === terrain.key) continue;

        let bucket = buckets.get(neighbour.colour);
        if (!bucket) {
          bucket = [];
          buckets.set(neighbour.colour, bucket);
        }
        const [ai, bi] = hexEdgeCorners(d);
        for (const point of edgeStipple(index, d, 8, corners[ai]!, corners[bi]!, depth)) {
          bucket.push({ x: screen.x + point.x, y: screen.y + point.y });
        }
      }
    });

    // Round grains, not squares. Square specks were tried first and they read as dead
    // pixels — the eye recognises an axis-aligned rectangle instantly and calls it a
    // fault, whereas the same amount of ink as round grain reads as stippling.
    const grain = Math.max(0.6, size * 0.055);
    for (const [colour, points] of buckets) {
      ctx.fillStyle = colour + '73';
      ctx.beginPath();
      for (const point of points) {
        ctx.moveTo(point.x + grain, point.y);
        ctx.arc(point.x, point.y, grain, 0, Math.PI * 2);
      }
      ctx.fill();
    }
  }

  // ── relief ──────────────────────────────────────────────────────────────────
  // Hills and mountains drawn as shapes standing on the ground, lit from the north-west
  // and casting a shadow south-east. Everything else on a strategy map is symbolic and
  // has to be decoded; height is the one thing the eye can simply perceive, and this is
  // the pass that lets it.
  if (size >= RELIEF_TILE_SIZE) {
    forEachVisibleTile(map, camera, (col, rawCol, row) => {
      const index = tileIndex(map, col, row);
      if (index === -1 || seen(index) === VIS_UNKNOWN) return;
      const terrain = content.terrain.get(map.terrain[index]!);
      const kind = reliefFor(terrain.key);
      if (kind === 'none') return;

      const centre = tileCentre(rawCol, row, size);
      const screen = worldToScreen(camera, centre.x, centre.y);
      drawRelief(ctx, screen.x, screen.y, size, kind, index, terrain.colour, corners);
    });
  }

  // ── scatter ─────────────────────────────────────────────────────────────────
  // A few marks per tile so ground has grain: trees on forest, dunes on desert, rock
  // on hills. Every position comes from the tile's own index, so nothing moves when
  // the map is panned or zoomed — detail that crawls is worse than no detail at all.
  if (size >= SCATTER_TILE_SIZE) {
    forEachVisibleTile(map, camera, (col, rawCol, row) => {
      const index = tileIndex(map, col, row);
      if (index === -1 || seen(index) === VIS_UNKNOWN) return;

      const terrain = content.terrain.get(map.terrain[index]!);
      const feature = content.features.get(map.feature[index]!);
      // Bare hills and mountains already have silhouettes; scree on top of them is noise.
      if (feature.key === 'none' && reliefFor(terrain.key) !== 'none') return;
      const kind = scatterFor(terrain.key, feature.key);
      if (kind === 'none') return;

      const centre = tileCentre(rawCol, row, size);
      const screen = worldToScreen(camera, centre.x, centre.y);
      drawScatter(ctx, screen.x, screen.y, size, kind, index);
    });
  }

  // ── worked ground ───────────────────────────────────────────────────────────
  // Fields, orchards, plantations, mines and quarries — see improvements.ts. Drawn this
  // early deliberately: a river runs through farmland, a road crosses it, and the wheat
  // that justified the farm sits on top of it. Drawn after them, the fields would bury
  // all three.
  if (detail) {
    forEachVisibleTile(map, camera, (col, rawCol, row) => {
      const index = tileIndex(map, col, row);
      if (index === -1 || seen(index) === VIS_UNKNOWN) return;
      const id = improvementAt(index);
      if (!id) return;
      const centre = tileCentre(rawCol, row, size);
      const screen = worldToScreen(camera, centre.x, centre.y);
      drawImprovementArt(ctx, content.improvements.get(id).art, screen.x, screen.y, size, index, corners);
    });
  }

  // ── grid, rivers and resources, in their own passes ─────────────────────────
  // Separate passes rather than one loop: a river drawn immediately after its tile
  // would be painted over by the next tile's fill, and the seams would show.

  if (options.showGrid && size >= GRID_TILE_SIZE) {
    ctx.strokeStyle = 'rgba(28, 22, 12, 0.085)';
    ctx.lineWidth = 1;
    forEachVisibleTile(map, camera, (col, rawCol, row) => {
      const gridIndex = tileIndex(map, col, row);
      if (gridIndex === -1 || seen(gridIndex) === VIS_UNKNOWN) return;
      const centre = tileCentre(rawCol, row, size);
      const screen = worldToScreen(camera, centre.x, centre.y);
      ctx.beginPath();
      ctx.moveTo(screen.x + corners[0]!.x, screen.y + corners[0]!.y);
      for (let i = 1; i < 6; i++) ctx.lineTo(screen.x + corners[i]!.x, screen.y + corners[i]!.y);
      ctx.closePath();
      ctx.stroke();
    });
  }

  if (options.showRivers) {
    ctx.strokeStyle = '#5FA8DC';
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    forEachVisibleTile(map, camera, (col, rawCol, row) => {
      const index = tileIndex(map, col, row);
      if (index === -1 || seen(index) === VIS_UNKNOWN) return;
      const mask = map.rivers[index]!;
      if (mask === 0) return;
      // River tracing marks both tiles of every step, so the tile a river empties into
      // carries a bit as well — which drew a river stub out across the open sea. The
      // mask has to stay symmetric because validateWorld checks it (ADR-0009), so the
      // fix belongs here: only land draws. The last land tile still draws its half
      // spoke seaward, which reads as a river mouth stopping at the coast.
      if (content.terrain.get(map.terrain[index]!).water) return;

      const centre = tileCentre(rawCol, row, size);
      const screen = worldToScreen(camera, centre.x, centre.y);

      // Width falls with elevation, so a river broadens as it runs to the sea. A
      // headwater and an estuary drawn the same width is the giveaway that a map was
      // generated rather than surveyed.
      const height = map.elevation[index]! / 255;
      ctx.lineWidth = Math.max(1.2, size * (0.06 + (1 - height) * 0.1));

      // A river is drawn as spokes from the tile centre toward each linked
      // neighbour. Neighbouring tiles draw their own halves, so the courses join up
      // into a continuous line without anyone tracking whole rivers.
      // Every spoke of this tile in one path, stroked twice. Bare blue disappears
      // against pale desert and pale tundra; a dark casing under it keeps a watercourse
      // legible on any ground at all, which is how rivers are drawn on real maps.
      //
      // Each spoke is a curve, not a line. Two things make a river meander rather than
      // form a lattice: the point where a tile's courses converge is nudged off centre,
      // and each spoke bows sideways on the way to the edge. Both are stable per tile,
      // and the bow is seeded from the *edge* so the tile next door bends its half the
      // same way — see riverBend. The mouth itself never moves.
      const water = ctx.lineWidth;
      const node = riverNode(index, size);
      const source = { x: screen.x + node.x, y: screen.y + node.y };
      neighborsByDirection(map, index, neighbourScratch);

      ctx.beginPath();
      for (let d = 0; d < 6; d++) {
        if ((mask & (1 << d)) === 0) continue;
        // Reach exactly halfway to the neighbour's centre. Adjacent centres are
        // sqrt(3) tile-sizes apart, so anything less leaves the two halves of a
        // course as disconnected dashes.
        const reach = (Math.sqrt(3) / 2) * size;
        const angle = DIRECTION_ANGLES[d]!;
        const mouth = { x: screen.x + Math.cos(angle) * reach, y: screen.y + Math.sin(angle) * reach };
        const bend = riverBend(index, neighbourScratch[d]!, source, mouth, size * 0.26);
        ctx.moveTo(source.x, source.y);
        ctx.quadraticCurveTo(bend.x, bend.y, mouth.x, mouth.y);
      }
      ctx.strokeStyle = 'rgba(16, 42, 62, 0.4)';
      ctx.lineWidth = water + Math.max(1, size * 0.045);
      ctx.stroke();
      ctx.strokeStyle = '#63B2E0';
      ctx.lineWidth = water;
      ctx.stroke();
    });
  }

  // ── roads ───────────────────────────────────────────────────────────────────
  // A road is a network, not a picture: each road tile draws a curved spoke toward
  // every neighbour that is also a road or a city, the same half-and-half construction
  // as rivers so the courses join without anyone tracing whole roads. Cities take part
  // as junctions, which is what makes a road visibly *arrive* somewhere.
  //
  // Everything goes into one path, stroked twice — dark bed, then pale surface — and
  // that ordering is the reason for the single path. Stroked tile by tile, each tile's
  // dark bed would be painted over the pale surface of the tile before it, and every
  // join would show a seam.
  if (detail) {
    const roads = new Path2D();
    let anyRoad = false;
    // Roads are their own layer (ADR-0055), and every city stands on one, so a city is
    // simply a road junction drawn from its centre.
    const isRoad = (index: number): boolean => scene.road(index);

    forEachVisibleTile(map, camera, (col, rawCol, row) => {
      const index = tileIndex(map, col, row);
      if (index === -1 || seen(index) === VIS_UNKNOWN) return;
      const road = isRoad(index);
      const city = cityAt(index);
      if (!road) return;

      const centre = tileCentre(rawCol, row, size);
      const screen = worldToScreen(camera, centre.x, centre.y);
      // A road runs straighter than a river, so its junction wanders less, and it uses
      // its own salts so a road beside a river does not trace the river's exact curve.
      const node = city ? { x: 0, y: 0 } : riverNode(index, size * 0.55, 96);
      const source = { x: screen.x + node.x, y: screen.y + node.y };
      neighborsByDirection(map, index, neighbourScratch);

      let links = 0;
      for (let d = 0; d < 6; d++) {
        const other = neighbourScratch[d]!;
        if (other === -1 || seen(other) === VIS_UNKNOWN) continue;
        if (!isRoad(other)) continue;

        const reach = (Math.sqrt(3) / 2) * size;
        const angle = DIRECTION_ANGLES[d]!;
        const mouth = { x: screen.x + Math.cos(angle) * reach, y: screen.y + Math.sin(angle) * reach };
        const bend = riverBend(index, other, source, mouth, size * 0.1, 98);
        roads.moveTo(source.x, source.y);
        roads.quadraticCurveTo(bend.x, bend.y, mouth.x, mouth.y);
        links++;
      }

      // A lone stretch of road, not yet joined to anything: a short track through the tile.
      if (!city && links === 0) {
        const a = tileNoise(index, 99) * Math.PI;
        const half = size * 0.42;
        roads.moveTo(source.x - Math.cos(a) * half, source.y - Math.sin(a) * half);
        roads.lineTo(source.x + Math.cos(a) * half, source.y + Math.sin(a) * half);
        links++;
      }
      if (links > 0) anyRoad = true;
    });

    if (anyRoad) {
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.strokeStyle = 'rgba(66, 48, 28, 0.78)';
      ctx.lineWidth = Math.max(2.5, size * 0.13);
      ctx.stroke(roads);
      ctx.strokeStyle = '#D6C194';
      ctx.lineWidth = Math.max(1.2, size * 0.07);
      ctx.stroke(roads);
    }
  }

  if (options.showResources && detail) {
    forEachVisibleTile(map, camera, (col, rawCol, row) => {
      const index = tileIndex(map, col, row);
      if (index === -1 || seen(index) === VIS_UNKNOWN) return;
      const resourceId = map.resource[index]!;
      if (resourceId === 0 || options.resourceSeen?.(resourceId) === false) return;

      const resource = content.resources.get(resourceId);
      const centre = tileCentre(rawCol, row, size);
      const screen = worldToScreen(camera, centre.x, centre.y);

      if (size >= ICON_TILE_SIZE) {
        const water = content.terrain.get(map.terrain[index]!).water;
        drawResourceIcon(ctx, resource.icon, screen.x, screen.y - size * 0.06, size, resource.colour, water);
        return;
      }

      const radius = size * 0.26;

      ctx.beginPath();
      ctx.arc(screen.x, screen.y - size * 0.1, radius, 0, Math.PI * 2);
      ctx.fillStyle = resource.colour;
      ctx.fill();
      ctx.strokeStyle = 'rgba(0,0,0,0.55)';
      ctx.lineWidth = Math.max(1, size * 0.05);
      ctx.stroke();
    });
  }

  // ── paper ───────────────────────────────────────────────────────────────────
  // A faint grain over the whole world. It does almost nothing on any single tile and
  // a great deal to all of them together: flat canvas fills read as *screen*, and a
  // little tooth reads as something printed. Laid down after the ground and before
  // anything symbolic, so units, cities and icons stay crisp on top of it.
  if (detail) {
    const grain = paperGrain(ctx);
    if (grain) {
      ctx.save();
      // Anchored to the world rather than the window, so the tooth sits still on the
      // ground while the map is dragged instead of swimming over the top of it.
      const ox = -(((camera.x % GRAIN_SIZE) + GRAIN_SIZE) % GRAIN_SIZE);
      const oy = -(((camera.y % GRAIN_SIZE) + GRAIN_SIZE) % GRAIN_SIZE);
      ctx.translate(ox, oy);
      ctx.fillStyle = grain;
      ctx.fillRect(0, 0, camera.viewportWidth + GRAIN_SIZE, camera.viewportHeight + GRAIN_SIZE);
      ctx.restore();
    }
  }

  // ── borders ─────────────────────────────────────────────────────────────────
  // A wash of the owner's colour rather than an outline: outlines on a hex grid need
  // edge-by-edge tracing, and a tint reads territory faster at every zoom anyway.
  if (options.showBorders) {
    forEachVisibleTile(map, camera, (col, rawCol, row) => {
      const index = tileIndex(map, col, row);
      if (index === -1 || seen(index) === VIS_UNKNOWN) return;
      const colour = scene.ownerColour(index);
      if (colour === null) return;

      const centre = tileCentre(rawCol, row, size);
      const screen = worldToScreen(camera, centre.x, centre.y);
      ctx.fillStyle = colour + '33';
      fillHex(ctx, screen.x, screen.y, corners);
      // The frontier line, so a border that culture moves can be seen to move (ADR-0057).
      neighborsByDirection(map, index, neighbourScratch);
      const edge = frontierPath(corners, scene.ownerColour, (i) => seen(i) !== VIS_UNKNOWN, colour, neighbourScratch);
      if (edge) {
        ctx.save();
        ctx.translate(screen.x, screen.y);
        ctx.strokeStyle = colour;
        ctx.lineWidth = Math.max(1.5, size * 0.07);
        ctx.lineCap = 'round';
        ctx.stroke(edge);
        ctx.restore();
      }
    });
  }

  if (options.workedBy !== null && detail) {
    ctx.strokeStyle = 'rgba(255,255,255,0.5)';
    ctx.lineWidth = Math.max(1, size * 0.05);
    forEachVisibleTile(map, camera, (col, rawCol, row) => {
      const index = tileIndex(map, col, row);
      if (index === -1 || map.workedBy[index] !== options.workedBy) return;
      const centre = tileCentre(rawCol, row, size);
      const screen = worldToScreen(camera, centre.x, centre.y);
      ctx.beginPath();
      ctx.arc(screen.x, screen.y, size * 0.16, 0, Math.PI * 2);
      ctx.stroke();
    });
  }

  // ── the fog of memory ───────────────────────────────────────────────────────
  // Remembered but unobserved ground, dimmed. Unknown ground was never drawn at all.
  if (scene.viewer !== null) {
    ctx.fillStyle = 'rgba(6, 12, 22, 0.5)';
    forEachVisibleTile(map, camera, (col, rawCol, row) => {
      const index = tileIndex(map, col, row);
      if (index === -1 || seen(index) !== VIS_EXPLORED) return;
      const centre = tileCentre(rawCol, row, size);
      const screen = worldToScreen(camera, centre.x, centre.y);
      fillHex(ctx, screen.x, screen.y, corners);
    });
  }

  // ── movement overlay for the selected unit ──────────────────────────────────
  if (options.reachable && options.reachable.size > 0) {
    ctx.fillStyle = 'rgba(255, 255, 255, 0.13)';
    forEachVisibleTile(map, camera, (col, rawCol, row) => {
      const index = tileIndex(map, col, row);
      if (index === -1 || !options.reachable!.has(index)) return;
      const centre = tileCentre(rawCol, row, size);
      const screen = worldToScreen(camera, centre.x, centre.y);
      fillHex(ctx, screen.x, screen.y, corners);
    });
  }

  if (options.path && options.path.length > 0) {
    ctx.fillStyle = 'rgba(255, 236, 170, 0.85)';
    for (const index of options.path) {
      const col = index % map.width;
      const row = (index - col) / map.width;
      if (row < range.minRow || row > range.maxRow) continue;
      const rawCol = nearestVisibleColumn(camera, map, col);
      const centre = tileCentre(rawCol, row, size);
      const screen = worldToScreen(camera, centre.x, centre.y);
      ctx.beginPath();
      ctx.arc(screen.x, screen.y, Math.max(1.5, size * 0.13), 0, Math.PI * 2);
      ctx.fill();
    }
  }

  // ── cities ──────────────────────────────────────────────────────────────────
  // A visible city is drawn from the live state; a remembered one from the viewer's
  // memory of it. That distinction is the whole reason CivMemory exists — without it
  // a rival's city would appear inside fog the player has not walked through since.
  forEachVisibleTile(map, camera, (col, rawCol, row) => {
    const index = tileIndex(map, col, row);
    if (index === -1) return;
    const city = scene.city(index);
    if (!city) return;
    const { name, population } = city;

    const centre = tileCentre(rawCol, row, size);
    const screen = worldToScreen(camera, centre.x, centre.y);
    drawCity(ctx, screen.x, screen.y, size, city.colour, population, detail);

    // A city building nothing pours its production away every turn, and it is the one
    // kind of neglect that is invisible from the map otherwise.
    if (city.id !== null && options.idleCities?.has(city.id) && detail) {
      ctx.beginPath();
      ctx.arc(screen.x + size * 0.42, screen.y - size * 0.42, size * 0.17, 0, Math.PI * 2);
      ctx.fillStyle = '#E08578';
      ctx.fill();
      ctx.strokeStyle = 'rgba(10,16,26,0.9)';
      ctx.lineWidth = Math.max(1, size * 0.04);
      ctx.stroke();
    }

    if (size >= GRID_TILE_SIZE) {
      ctx.font = `600 ${Math.max(9, Math.round(size * 0.42))}px system-ui, sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'top';
      const label = `${name} ${population}`;
      const y = screen.y + size * 0.62;
      ctx.strokeStyle = 'rgba(8,14,22,0.9)';
      ctx.lineWidth = Math.max(2, size * 0.12);
      ctx.strokeText(label, screen.x, y);
      ctx.fillStyle = '#F2F6F4';
      ctx.fillText(label, screen.x, y);
    }
  });

  // ── units, only where the viewer can actually see them (the scene decides) ──
  // One glyph per stack: the selected unit if it is in it, otherwise the stack's
  // defender. Every unit drawn on top of the last made a stack of six look like one.
  for (const stack of scene.stacks) {
    const col = stack.index % map.width;
    const row = (stack.index - col) / map.width;
    if (row < range.minRow || row > range.maxRow) continue;
    const rawCol = nearestVisibleColumn(camera, map, col);
    if (rawCol < range.minCol || rawCol > range.maxCol) continue;

    const centre = tileCentre(rawCol, row, size);
    const screen = worldToScreen(camera, centre.x, centre.y);
    const shown = stack.units.find((u) => u.id === options.selectedUnit) ?? stack.units[0]!;
    drawUnit(ctx, screen.x, screen.y, size, shown.glyph, shown.colour);

    if (detail) drawUnitBadges(ctx, screen.x, screen.y, size, shown, stack.units.length);

    if (options.selectedUnit === shown.id) {
      ctx.beginPath();
      ctx.arc(screen.x, screen.y, size * 0.62, 0, Math.PI * 2);
      ctx.strokeStyle = '#FFFFFF';
      ctx.lineWidth = Math.max(1.5, size * 0.08);
      ctx.stroke();
    }
  }

  // ── hover highlight, last so nothing paints over it ─────────────────────────
  if (options.hover) {
    const nearest = nearestVisibleColumn(camera, map, options.hover.col);
    const centre = tileCentre(nearest, options.hover.row, size);
    const screen = worldToScreen(camera, centre.x, centre.y);
    ctx.beginPath();
    ctx.moveTo(screen.x + corners[0]!.x, screen.y + corners[0]!.y);
    for (let i = 1; i < 6; i++) ctx.lineTo(screen.x + corners[i]!.x, screen.y + corners[i]!.y);
    ctx.closePath();
    ctx.strokeStyle = 'rgba(255,255,255,0.9)';
    ctx.lineWidth = Math.max(1.5, size * 0.09);
    ctx.stroke();
  }

  // ── vignette ────────────────────────────────────────────────────────────────
  // The corners of a canvas are the one place nothing interesting ever happens, and
  // leaving them at full brightness is what makes a rendered map feel like a spreadsheet
  // of coloured cells. A little darkening there pushes the eye to the middle.
  const cx = camera.viewportWidth / 2;
  const cy = camera.viewportHeight / 2;
  const vignette = ctx.createRadialGradient(
    cx,
    cy,
    Math.min(cx, cy) * 0.55,
    cx,
    cy,
    Math.hypot(cx, cy),
  );
  vignette.addColorStop(0, 'rgba(0,0,0,0)');
  vignette.addColorStop(1, 'rgba(4, 9, 16, 0.32)');
  ctx.fillStyle = vignette;
  ctx.fillRect(0, 0, camera.viewportWidth, camera.viewportHeight);

  return { tilesDrawn, frameMs: performance.now() - started };
}

/** How many of a tile's six neighbours are dry land. Drives the shoal band round coasts. */
function landNeighbours(map: TileGrid, content: Content, index: number, scratch: Int32Array): number {
  neighborsByDirection(map, index, scratch);
  let count = 0;
  for (let d = 0; d < 6; d++) {
    const other = scratch[d]!;
    if (other !== -1 && !content.terrain.get(map.terrain[other]!).water) count++;
  }
  return count;
}

/** Side of the noise tile used for the paper grain. Kept small; it repeats. */
const GRAIN_SIZE = 96;
let grainPattern: CanvasPattern | null = null;

/**
 * A tile of paper tooth, built once and repeated.
 *
 * Deliberately almost invisible — the alpha peaks around 7% — because grain you can
 * actually see reads as dirt. Built from the same tile hash as everything else, so it
 * is identical between runs and there is no `Math.random` anywhere in the renderer.
 */
function paperGrain(ctx: CanvasRenderingContext2D): CanvasPattern | null {
  if (grainPattern) return grainPattern;

  const tile = document.createElement('canvas');
  tile.width = GRAIN_SIZE;
  tile.height = GRAIN_SIZE;
  const paint = tile.getContext('2d');
  if (!paint) return null;

  const image = paint.createImageData(GRAIN_SIZE, GRAIN_SIZE);
  for (let i = 0; i < GRAIN_SIZE * GRAIN_SIZE; i++) {
    const n = tileNoise(i, 99);
    const value = n < 0.5 ? 0 : 255;
    image.data[i * 4] = value;
    image.data[i * 4 + 1] = value;
    image.data[i * 4 + 2] = value;
    image.data[i * 4 + 3] = Math.round(Math.abs(n - 0.5) * 2 * 18);
  }
  paint.putImageData(image, 0, 0);

  grainPattern = ctx.createPattern(tile, 'repeat');
  return grainPattern;
}

/**
 * Hills and mountains, drawn as ground that has shape.
 *
 * Light comes from the north-west throughout — an arbitrary choice, but it has to be
 * the *same* arbitrary choice everywhere or the relief stops reading as relief and
 * starts reading as decoration. Each ridge gets a shadow on the ground beneath it, a
 * lit face and a shaded one, and the whole lot is clipped to the hex so a mountain
 * never spills onto the plain next door.
 */
function drawRelief(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  size: number,
  kind: ReliefKind,
  index: number,
  base: string,
  corners: Array<{ x: number; y: number }>,
): void {
  const rgb = parseHex(base);
  const lit = toCss(shade(rgb, kind === 'mountains' ? 0.14 : 0.2));
  const dark = toCss(shade(rgb, kind === 'mountains' ? -0.44 : -0.3));

  ctx.save();
  ctx.beginPath();
  ctx.moveTo(x + corners[0]!.x, y + corners[0]!.y);
  for (let i = 1; i < 6; i++) ctx.lineTo(x + corners[i]!.x, y + corners[i]!.y);
  ctx.closePath();
  ctx.clip();

  for (const ridge of ridges(index, kind, size)) {
    const px = x + ridge.x;
    const py = y + ridge.y;
    const half = ridge.width / 2;
    const high = ridge.height;

    // The shadow on the ground. Without it the shape floats rather than stands.
    ctx.beginPath();
    ctx.ellipse(px + half * 0.42, py + high * 0.06, half * 1.1, Math.max(1, high * 0.2), 0, 0, Math.PI * 2);
    ctx.fillStyle = 'rgba(28, 22, 16, 0.26)';
    ctx.fill();

    if (kind === 'mountains') {
      // The ridge running down from the peak meets the ground RIGHT of centre, so the
      // lit face is broader than the shaded one. Splitting a narrow triangle straight
      // down the middle was the first attempt and every mountain came out looking like
      // a sail: a vertical division reads as a mast, and two equal halves read as
      // cloth. Off-centre and wide reads as rock.
      const foot = px + half * 0.28;

      ctx.beginPath();
      ctx.moveTo(px, py - high);
      ctx.lineTo(px - half, py);
      ctx.lineTo(foot, py);
      ctx.closePath();
      ctx.fillStyle = lit;
      ctx.fill();

      ctx.beginPath();
      ctx.moveTo(px, py - high);
      ctx.lineTo(px + half, py);
      ctx.lineTo(foot, py);
      ctx.closePath();
      ctx.fillStyle = dark;
      ctx.fill();

      // Snow, on the tallest peaks only. Generous caps were the first thing tried and
      // they turned every range into a row of little white sails: the eye reads the
      // bright shape and loses the dark mass under it, which is the part that means
      // "mountain". A cap is a hint at the very top, or it is nothing.
      if (high > size * 0.77) {
        ctx.beginPath();
        ctx.moveTo(px, py - high);
        ctx.lineTo(px - half * 0.22, py - high * 0.76);
        ctx.lineTo(px - half * 0.06, py - high * 0.83);
        ctx.lineTo(px + half * 0.09, py - high * 0.75);
        ctx.lineTo(px + half * 0.2, py - high * 0.79);
        ctx.closePath();
        ctx.fillStyle = 'rgba(228, 237, 244, 0.6)';
        ctx.fill();
      }
    } else {
      // A hump: the shaded body, then the lit crown set up and to the left of it.
      ctx.beginPath();
      ctx.ellipse(px, py, half, high, 0, Math.PI, Math.PI * 2);
      ctx.closePath();
      ctx.fillStyle = dark;
      ctx.fill();

      ctx.beginPath();
      ctx.ellipse(px - half * 0.17, py, half * 0.8, high * 0.86, 0, Math.PI, Math.PI * 2);
      ctx.closePath();
      ctx.fillStyle = lit;
      ctx.fill();
    }
  }

  ctx.restore();
}

function fillHex(ctx: CanvasRenderingContext2D, x: number, y: number, corners: Array<{ x: number; y: number }>): void {
  ctx.beginPath();
  ctx.moveTo(x + corners[0]!.x, y + corners[0]!.y);
  for (let i = 1; i < 6; i++) ctx.lineTo(x + corners[i]!.x, y + corners[i]!.y);
  ctx.closePath();
  ctx.fill();
}

/**
 * A unit, as a flat shape in its owner's colour.
 *
 * Placeholder art with a real job: the shapes have to be told apart at the smallest
 * zoom the game allows, which rules out anything detailed. Proper unit art is Phase 14.
 */
export function drawUnit(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  size: number,
  glyph: 'chevron' | 'shield' | 'disc' | 'ship' | 'siege' | 'plane',
  colour: string,
): void {
  const r = size * 0.42;

  ctx.beginPath();
  ctx.arc(x, y, r * 1.12, 0, Math.PI * 2);
  ctx.fillStyle = 'rgba(12, 18, 28, 0.75)';
  ctx.fill();

  ctx.fillStyle = colour;
  ctx.strokeStyle = 'rgba(10, 16, 26, 0.9)';
  ctx.lineWidth = Math.max(1, size * 0.04);

  ctx.beginPath();
  if (glyph === 'chevron') {
    ctx.moveTo(x, y - r);
    ctx.lineTo(x + r * 0.85, y + r * 0.75);
    ctx.lineTo(x, y + r * 0.3);
    ctx.lineTo(x - r * 0.85, y + r * 0.75);
  } else if (glyph === 'shield') {
    ctx.moveTo(x, y - r);
    ctx.lineTo(x + r * 0.8, y - r * 0.45);
    ctx.lineTo(x + r * 0.8, y + r * 0.25);
    ctx.lineTo(x, y + r);
    ctx.lineTo(x - r * 0.8, y + r * 0.25);
    ctx.lineTo(x - r * 0.8, y - r * 0.45);
  } else if (glyph === 'ship') {
    // A hull with a sail above it: reads as a boat at any size, and as nothing else.
    ctx.moveTo(x - r * 0.95, y + r * 0.2);
    ctx.lineTo(x + r * 0.95, y + r * 0.2);
    ctx.lineTo(x + r * 0.6, y + r * 0.75);
    ctx.lineTo(x - r * 0.6, y + r * 0.75);
    ctx.closePath();
    ctx.moveTo(x - r * 0.05, y - r * 0.95);
    ctx.lineTo(x + r * 0.7, y + r * 0.05);
    ctx.lineTo(x - r * 0.05, y + r * 0.05);
  } else if (glyph === 'plane') {
    // Seen from above: a fuselage, swept wings and a tailplane.
    ctx.moveTo(x, y - r);
    ctx.lineTo(x + r * 0.14, y - r * 0.2);
    ctx.lineTo(x + r * 0.95, y + r * 0.2);
    ctx.lineTo(x + r * 0.95, y + r * 0.38);
    ctx.lineTo(x + r * 0.14, y + r * 0.22);
    ctx.lineTo(x + r * 0.12, y + r * 0.62);
    ctx.lineTo(x + r * 0.42, y + r * 0.85);
    ctx.lineTo(x + r * 0.42, y + r * 0.98);
    ctx.lineTo(x, y + r * 0.86);
    ctx.lineTo(x - r * 0.42, y + r * 0.98);
    ctx.lineTo(x - r * 0.42, y + r * 0.85);
    ctx.lineTo(x - r * 0.12, y + r * 0.62);
    ctx.lineTo(x - r * 0.14, y + r * 0.22);
    ctx.lineTo(x - r * 0.95, y + r * 0.38);
    ctx.lineTo(x - r * 0.95, y + r * 0.2);
    ctx.lineTo(x - r * 0.14, y - r * 0.2);
  } else if (glyph === 'siege') {
    // A barrel cocked upward over a wheel: an engine for walls, not a soldier.
    ctx.moveTo(x - r * 0.85, y + r * 0.15);
    ctx.lineTo(x + r * 0.55, y - r * 0.85);
    ctx.lineTo(x + r * 0.85, y - r * 0.45);
    ctx.lineTo(x - r * 0.45, y + r * 0.5);
    ctx.closePath();
    ctx.moveTo(x + r * 0.62, y + r * 0.42);
    ctx.arc(x + r * 0.05, y + r * 0.42, r * 0.57, 0, Math.PI * 2);
  } else {
    ctx.arc(x, y, r * 0.8, 0, Math.PI * 2);
  }
  ctx.closePath();
  ctx.fill();
  ctx.stroke();
}

/**
 * A health bar when a unit is hurt, and a count when it is not alone.
 *
 * Health is the one number a battle changes, and a wounded unit that looks whole is a
 * unit the player sends into a second fight it cannot survive.
 */
export function drawUnitBadges(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  size: number,
  unit: SceneUnit,
  count: number,
): void {
  if (unit.hp < unit.maxHp) {
    const w = size * 0.8;
    const h = Math.max(2, size * 0.09);
    const left = x - w / 2;
    const top = y - size * 0.66;
    ctx.fillStyle = 'rgba(10, 16, 26, 0.85)';
    ctx.fillRect(left - 1, top - 1, w + 2, h + 2);
    const share = Math.max(0, unit.hp / unit.maxHp);
    ctx.fillStyle = share > 0.66 ? '#7FC47A' : share > 0.33 ? '#E2C15A' : '#E0705E';
    ctx.fillRect(left, top, w * share, h);
  }
  // Rank: a gold chevron for each step above Green, at the shoulder.
  for (let i = 0; i < unit.rank; i++) {
    const cx = x - size * 0.44;
    const cy = y - size * 0.3 + i * size * 0.13;
    const w = size * 0.12;
    ctx.beginPath();
    ctx.moveTo(cx - w, cy);
    ctx.lineTo(cx, cy + w * 0.8);
    ctx.lineTo(cx + w, cy);
    ctx.strokeStyle = 'rgba(10, 16, 26, 0.9)';
    ctx.lineWidth = Math.max(2.5, size * 0.09);
    ctx.stroke();
    ctx.strokeStyle = '#E8C25A';
    ctx.lineWidth = Math.max(1.2, size * 0.045);
    ctx.stroke();
  }
  // Cargo: how many are aboard, under the hull.
  if (unit.cargo > 0) {
    const r = Math.max(5, size * 0.18);
    const bx = x + size * 0.42;
    const by = y + size * 0.38;
    ctx.fillStyle = 'rgba(10, 16, 26, 0.9)';
    ctx.fillRect(bx - r, by - r * 0.8, r * 2, r * 1.6);
    ctx.fillStyle = '#F2F6F4';
    ctx.font = `700 ${Math.round(r * 1.2)}px system-ui, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(String(unit.cargo), bx, by + 0.5);
  }
  if (count > 1) {
    const bx = x + size * 0.42;
    const by = y - size * 0.4;
    const r = Math.max(5, size * 0.2);
    ctx.beginPath();
    ctx.arc(bx, by, r, 0, Math.PI * 2);
    ctx.fillStyle = 'rgba(242, 246, 244, 0.95)';
    ctx.fill();
    ctx.fillStyle = '#121A24';
    ctx.font = `700 ${Math.round(r * 1.3)}px system-ui, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(String(count), bx, by + 0.5);
  }
}

/**
 * A city: a walled block in its owner's colour, growing a little with population.
 *
 * Placeholder art, like the unit glyphs, but it has to survive being three pixels
 * across at minimum zoom, which rules out anything with detail in it.
 */
function drawCity(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  size: number,
  colour: string,
  population: number,
  detail: boolean,
): void {
  const r = size * (0.34 + Math.min(population, 12) * 0.012);

  ctx.beginPath();
  ctx.arc(x, y, r * 1.35, 0, Math.PI * 2);
  ctx.fillStyle = 'rgba(10, 16, 26, 0.8)';
  ctx.fill();

  ctx.fillStyle = colour;
  ctx.strokeStyle = 'rgba(10, 16, 26, 0.95)';
  ctx.lineWidth = Math.max(1, size * 0.05);
  ctx.beginPath();
  ctx.rect(x - r, y - r * 0.8, r * 2, r * 1.6);
  ctx.fill();
  ctx.stroke();

  if (detail) {
    // A notch of battlements, so a city is not merely a coloured square.
    ctx.fillStyle = 'rgba(10, 16, 26, 0.85)';
    const merlon = r * 0.36;
    for (let i = -1; i <= 1; i++) {
      ctx.fillRect(x + i * merlon * 1.6 - merlon / 2, y - r * 0.8, merlon, merlon * 0.7);
    }
  }
}

/**
 * The marks that give ground its grain.
 *
 * Deliberately tiny and low-contrast. These are texture, not symbols: the eye should
 * register that forest is *busy* and desert is *rippled* without ever stopping to look
 * at an individual tree.
 */
function drawScatter(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  size: number,
  kind: ScatterKind,
  index: number,
): void {
  const spread = size * 0.4;

  if (kind === 'trees' || kind === 'conifers') {
    // A wood from above has no individual trees in it — it has a canopy with a lumpy
    // edge, a dark underside and a lit top. Three passes over the same overlapping
    // masses: shadow, body, highlight. Separate dots give you polka dots.
    const count = 5 + Math.floor(tileNoise(index, 2) * 3);
    const blobs = canopyBlobs(index, count, spread * 0.92, size * 0.2);

    const shape = (bx: number, by: number, r: number): void => {
      ctx.beginPath();
      if (kind === 'conifers') {
        ctx.moveTo(bx, by - r * 1.5);
        ctx.lineTo(bx + r * 0.82, by + r * 0.7);
        ctx.lineTo(bx - r * 0.82, by + r * 0.7);
      } else {
        ctx.arc(bx, by, r, 0, Math.PI * 2);
      }
      ctx.closePath();
      ctx.fill();
    };

    ctx.fillStyle = 'rgba(10, 24, 14, 0.4)';
    for (const blob of blobs) shape(x + blob.x + size * 0.07, y + blob.y + size * 0.08, blob.r);

    ctx.fillStyle = 'rgba(30, 62, 34, 0.62)';
    for (const blob of blobs) shape(x + blob.x, y + blob.y, blob.r);

    // The lit top of the canopy, offset toward the light. Small and low-contrast: it
    // should register as sunlight on leaves, not as a second set of shapes.
    ctx.fillStyle = 'rgba(134, 178, 100, 0.34)';
    for (const blob of blobs) shape(x + blob.x - size * 0.05, y + blob.y - size * 0.06, blob.r * 0.52);
    return;
  }

  if (kind === 'dunes') {
    ctx.strokeStyle = 'rgba(120, 96, 52, 0.36)';
    ctx.lineWidth = Math.max(1, size * 0.045);
    for (const point of scatterPoints(index, 3, spread * 0.75)) {
      ctx.beginPath();
      ctx.arc(x + point.x, y + point.y, size * 0.17, Math.PI * 1.15, Math.PI * 1.85);
      ctx.stroke();
    }
    return;
  }

  if (kind === 'rocks') {
    ctx.fillStyle = 'rgba(44, 40, 34, 0.42)';
    for (const point of scatterPoints(index, 3, spread * 0.8)) {
      ctx.beginPath();
      ctx.moveTo(x + point.x, y + point.y - size * 0.09);
      ctx.lineTo(x + point.x + size * 0.08, y + point.y + size * 0.06);
      ctx.lineTo(x + point.x - size * 0.08, y + point.y + size * 0.06);
      ctx.closePath();
      ctx.fill();
    }
    return;
  }

  if (kind === 'marsh') {
    ctx.strokeStyle = 'rgba(30, 62, 58, 0.45)';
    ctx.lineWidth = Math.max(1, size * 0.04);
    for (const point of scatterPoints(index, 4, spread * 0.8)) {
      ctx.beginPath();
      ctx.moveTo(x + point.x - size * 0.09, y + point.y);
      ctx.lineTo(x + point.x + size * 0.09, y + point.y);
      ctx.stroke();
    }
    return;
  }

  // Tussock: the faintest of all. Open country should read as open.
  ctx.strokeStyle = 'rgba(40, 52, 26, 0.22)';
  ctx.lineWidth = Math.max(1, size * 0.035);
  for (const point of scatterPoints(index, 3, spread * 0.85)) {
    ctx.beginPath();
    ctx.moveTo(x + point.x, y + point.y + size * 0.05);
    ctx.lineTo(x + point.x, y + point.y - size * 0.06);
    ctx.stroke();
  }
}

/** Walk the visible tiles, giving both the wrapped column (for data) and the raw one (for position). */
function forEachVisibleTile(
  map: TileGrid,
  camera: Camera,
  visit: (col: number, rawCol: number, row: number) => void,
): void {
  const range = visibleRange(camera, map);
  for (let row = range.minRow; row <= range.maxRow; row++) {
    for (let rawCol = range.minCol; rawCol <= range.maxCol; rawCol++) {
      visit(((rawCol % map.width) + map.width) % map.width, rawCol, row);
    }
  }
}

/**
 * On a wrapping map a tile has infinitely many world positions, one per lap. Pick the
 * copy nearest the current view, so a highlight on a tile near the seam appears where
 * the player is actually looking rather than a world-width away.
 */
function nearestVisibleColumn(camera: Camera, map: TileGrid, col: number): number {
  const range = visibleRange(camera, map);
  const centreRaw = (range.minCol + range.maxCol) / 2;
  const laps = Math.round((centreRaw - col) / map.width);
  return col + laps * map.width;
}
